// File: src/pages/ConnectWooCommerce.jsx

import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Page, Frame, Toast } from '@shopify/polaris';
import { AuthContext } from '../context/AuthContext';
import { SiteContext } from '../context/SiteContext';
import FlashCardCongrats from '../components/ui/trackerInstalled';
import OnboardingBanner from './OnboardingBanner';
import styles from '../pages/IntegrationsPage.module.css';

//const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';
const API = 'https://api.baobrain.com';
// Icons
const CheckIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);



const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const AnalyticsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </svg>
);

export default function ConnectWooCommerce() {
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const { token, setToken } = useContext(AuthContext);
  const { activeSite, setSites } = useContext(SiteContext);

  const initialSiteId = activeSite?.id || Number(params.get('siteId')) || state?.site?.id;
  const initialSiteToken = activeSite?.token || params.get('siteToken') || state?.site?.token;
  const initialGa4IdForPlugin = params.get('ga4Id') || state?.site?.ga4Id || '';
  const initialSiteUrl = activeSite?.url || state?.site?.url || '';

  const [site, setSite] = useState({
    id: initialSiteId,
    token: initialSiteToken,
    ga4Id: initialGa4IdForPlugin,
    url: initialSiteUrl,
  });

  const [propsList, setPropsList] = useState([]);
  const [propId, setPropId] = useState('');
  const [savedPropId, setSavedPropId] = useState('');
  const [propsLoading, setPropsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [disconnectingGa4, setDisconnectingGa4] = useState(false);

  const [registering, setRegistering] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // WordPress Plugin Redirect Flow Support
  const [wpReturnUrl, setWpReturnUrl] = useState(null);
  const [wpShopUrl, setWpShopUrl] = useState(null);
  const [creatingSite, setCreatingSite] = useState(false);
  const isFromWordPress = !!wpReturnUrl;

  // Track if Google Analytics is actually connected
  const [ga4Connected, setGa4Connected] = useState(false);

  // Carousel slide control (0 = welcome, 1 = GA4, 2 = download)
  const [slideIndex, setSlideIndex] = useState(0);

  // Inline confirmation toast — brief mid-step success messages between slides.
  // Pure UI, no logic dependency.
  const [toast, setToast] = useState(null); // { msg: string, key: number }
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Fire toast when GA4 connection completes
  const prevConnectedRef = React.useRef(false);
  useEffect(() => {
    if (loading) return;
    if (!prevConnectedRef.current && ga4Connected) {
      setToast({ msg: 'Google account connected successfully', key: Date.now() });
    }
    prevConnectedRef.current = ga4Connected;
  }, [ga4Connected, loading]);

  // Fire toast when site setup completes (id + token available)
  const isComplete = !!(site.id && site.token);
  const prevCompleteRef = React.useRef(false);
  useEffect(() => {
    if (!prevCompleteRef.current && isComplete) {
      setToast({ msg: 'Store registered successfully — plugin is ready to download', key: Date.now() });
    }
    prevCompleteRef.current = isComplete;
  }, [isComplete]);

  // Derived state
  //const isComplete = !!(site.id && site.token);
  const pluginUrl = (site.id && site.token) 
    ? `${API}/plugins/woocommerce/${site.id}/${site.token}/${propId || 'none'}` 
    : '#';

  // ============================================
  //  ALL ORIGINAL useEffects - PRESERVED
  // ============================================

  // Detect WordPress redirect params
  useEffect(() => {
    const returnUrl = params.get('return_url');
    const shopUrl = params.get('shop_url');

    if (returnUrl) {
      setWpReturnUrl(decodeURIComponent(returnUrl));
      setWpShopUrl(shopUrl ? decodeURIComponent(shopUrl) : null);
      sessionStorage.setItem('wp_return_url', decodeURIComponent(returnUrl));
      if (shopUrl) sessionStorage.setItem('wp_shop_url', decodeURIComponent(shopUrl));
    } else {
      const storedReturnUrl = sessionStorage.getItem('wp_return_url');
      if (storedReturnUrl) {
        setWpReturnUrl(storedReturnUrl);
        setWpShopUrl(sessionStorage.getItem('wp_shop_url'));
      }
    }
  }, [params]);

  // Auto-create site for WordPress users who don't have one
  useEffect(() => {
    if (!token || !isFromWordPress || site.id || creatingSite) return;

    const shopUrl = wpShopUrl || sessionStorage.getItem('wp_shop_url');
    if (!shopUrl) return;

    const createSiteForWordPress = async () => {
      setCreatingSite(true);
      setError('');

      try {
        // First check if user already has a site
        const sitesRes = await fetch(`${API}/api/sites`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (sitesRes.ok) {
          const existingSites = await sitesRes.json();
          if (existingSites.length > 0) {
            const existingSite = existingSites[0];
            setSite({
              id: existingSite.id,
              token: existingSite.token,
              url: existingSite.url,
              ga4Id: existingSite.ga4Id || '',
            });
            sessionStorage.setItem('woocommerce_site_id', existingSite.id.toString());
            sessionStorage.setItem('woocommerce_site_token', existingSite.token);
            setCreatingSite(false);
            setLoading(false);
            return;
          }
        }

        // No existing site - create one
        const siteName = new URL(shopUrl).hostname || 'My WooCommerce Store';
        const res = await fetch(`${API}/api/sites`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: siteName,
            url: shopUrl,
            platform: 'woocommerce',
          }),
        });

        if (res.status === 409) {
          // User already has max sites - fetch and use existing
          const sitesRes2 = await fetch(`${API}/api/sites`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (sitesRes2.ok) {
            const sites = await sitesRes2.json();
            if (sites.length > 0) {
              const existingSite = sites[0];
              setSite({
                id: existingSite.id,
                token: existingSite.token,
                url: existingSite.url,
                ga4Id: existingSite.ga4Id || '',
              });
              sessionStorage.setItem('woocommerce_site_id', existingSite.id.toString());
              sessionStorage.setItem('woocommerce_site_token', existingSite.token);
            }
          }
          setCreatingSite(false);
          setLoading(false);
          return;
        }

        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.detail || 'Failed to create site');
        }

        const created = await res.json();

        setSite({
          id: created.id,
          token: created.token,
          url: created.url,
          ga4Id: '',
        });

        if (setSites) {
          setSites(prev => [...prev, created]);
        }

        sessionStorage.setItem('woocommerce_site_id', created.id.toString());
        sessionStorage.setItem('woocommerce_site_token', created.token);

      } catch (err) {
        console.error('[ConnectWooCommerce] Error creating site:', err);
        setError(`Failed to set up your site: ${err.message}`);
      } finally {
        setCreatingSite(false);
        setLoading(false);
      }
    };

    createSiteForWordPress();
  }, [token, isFromWordPress, site.id, wpShopUrl, setSites, creatingSite]);

  // Redirect back to WordPress with credentials
  const redirectToWordPress = useCallback(() => {
    const returnUrl = wpReturnUrl || sessionStorage.getItem('wp_return_url');
    if (returnUrl && site.id && site.token) {
      const redirectUrl = new URL(returnUrl);
      redirectUrl.searchParams.set('site_id', site.id.toString());
      redirectUrl.searchParams.set('site_token', site.token);
      if (propId) redirectUrl.searchParams.set('ga4_id', propId);
      sessionStorage.removeItem('wp_return_url');
      sessionStorage.removeItem('wp_shop_url');
      window.location.href = redirectUrl.toString();
      return true;
    }
    return false;
  }, [wpReturnUrl, site.id, site.token, propId]);

  // Token capture from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const t = searchParams.get('token');
    if (t) {
      if (!token) {
        setToken(t);
      }
      // Mark OAuth return
      sessionStorage.setItem('wc_oauth_return', 'true');
      
      searchParams.delete('token');
      searchParams.delete('next');
      const newSearch = searchParams.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [token, setToken]);

  // After loading completes, check if we should skip to step 2
  useEffect(() => {
    if (loading || creatingSite) return;
    
    const oauthReturn = sessionStorage.getItem('wc_oauth_return');
    
    if (oauthReturn) {
      sessionStorage.removeItem('wc_oauth_return');
      setSlideIndex(1);
    } else if (ga4Connected) {
      setSlideIndex(1);
    }
  }, [loading, creatingSite, ga4Connected]);

  // Sync site state from activeSite context
  useEffect(() => {
    if (activeSite && (activeSite.id !== site.id || activeSite.token !== site.token)) {
      setSite(prevSite => ({
        ...prevSite,
        id: activeSite.id,
        token: activeSite.token,
        ga4Id: activeSite.ga4Id || prevSite.ga4Id || '',
        url: activeSite.url || prevSite.url || '',
      }));
    }
  }, [activeSite, site.id, site.token]);

  // Check site info
  useEffect(() => {
    if (!site.id || !site.token) {
      if (!isFromWordPress && !creatingSite) {
        setError('Site information is missing. Please start from "Add Site".');
      }
      setLoading(false);
      return;
    }
    setLoading(false);
  }, [site.id, site.token, isFromWordPress, creatingSite]);

  // Fetch site token if missing
  useEffect(() => {
    if (!site.token && site.id && token) {
      const fetchSiteToken = async () => {
        try {
          const res = await fetch(`${API}/api/sites/${site.id}/token`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) {
            const errorBody = await res.json();
            throw new Error(errorBody.detail || `Failed to fetch site token: ${res.status}`);
          }
          const data = await res.json();
          if (data.token) {
            setSite(s => ({ ...s, token: data.token }));
          } else {
            setError('Could not retrieve site token. Please try again or re-add the site.');
          }
        } catch (err) {
          console.error('[ConnectWooCommerce] Error fetching site token:', err);
          setError(`Error fetching site token: ${err.message}`);
        }
      };
      fetchSiteToken();
    }
  }, [site.id, site.token, token]);

  const resetGa4IntegrationStates = useCallback(() => {
    setPropsList([]);
    setPropId('');
    setSavedPropId('');
    setProcessing(false);
    setError('');
    setDisconnectingGa4(false);
    setGa4Connected(false);
  }, []);

  // Fetch sites from API
  useEffect(() => {
    if (!token) return;

    const fetchSites = async () => {
      try {
        const res = await fetch(`${API}/api/sites`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) return;

        const json = await res.json();
        const sites = Array.isArray(json) ? json : (json?.sites || json?.data || []);

        if (sites.length > 0) {
          if (initialSiteId) {
            const matchingSite = sites.find(s => s.id === Number(initialSiteId));
            if (matchingSite) {
              setSite(prevSite => ({
                ...prevSite,
                id: matchingSite.id,
                token: matchingSite.token,
                url: matchingSite.url || prevSite.url,
              }));
              sessionStorage.setItem('woocommerce_site_id', matchingSite.id.toString());
              sessionStorage.setItem('woocommerce_site_token', matchingSite.token);
            }
          } else {
            const firstSite = sites[0];
            setSite(prevSite => ({
              ...prevSite,
              id: firstSite.id,
              token: firstSite.token,
              url: firstSite.url || prevSite.url,
            }));
            sessionStorage.setItem('woocommerce_site_id', firstSite.id.toString());
            sessionStorage.setItem('woocommerce_site_token', firstSite.token);
          }
        }
      } catch (err) {
        console.error('[ConnectWooCommerce] Error fetching sites:', err);
      }
    };

    fetchSites();
  }, [token, initialSiteId]);

  // Fetch saved GA4 property
  const fetchSavedGa4Property = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/integrations/property`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        if (json.propertyId) {
          setSavedPropId(json.propertyId);
          setPropId(json.propertyId);
        }
      }
    } catch (err) {
      console.error('[ConnectWooCommerce] Saved GA4 property fetch error:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchSavedGa4Property();
  }, [fetchSavedGa4Property]);

  // Fetch GA4 properties
  const fetchGa4Properties = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setPropsLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/integrations/ga4-properties`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error('Authentication failed. Please re-connect your Google account.');
        }
        const errorText = await res.text();
        if (!errorText.includes('Not connected')) {
          throw new Error(`Failed to fetch GA4 properties: ${res.status} - ${errorText}`);
        }
        setLoading(false);
        setPropsLoading(false);
        return;
      }
      const list = await res.json();
      setPropsList(list);
      setGa4Connected(true);

      if (list.length > 0) {
        if (savedPropId && list.some(p => p.measurementId === savedPropId)) {
          setPropId(savedPropId);
        } else {
          setPropId(list[0].measurementId);
        }
      } else {
        setPropId('');
      }
    } catch (err) {
      console.error('[ConnectWooCommerce] GA4 properties fetch error', err);
      setError(`Failed to load GA4 properties: ${err.message}`);
    } finally {
      setLoading(false);
      setPropsLoading(false);
    }
  }, [token, savedPropId]);

  useEffect(() => {
    fetchGa4Properties();
  }, [fetchGa4Properties]);

  // ============================================
  //  ALL ORIGINAL HANDLERS - PRESERVED
  // ============================================

  const handleConnectGoogle = () => {
    let nextUrl = `/connect-woocommerce?siteId=${site.id}&siteToken=${encodeURIComponent(site.token)}${savedPropId ? `&ga4Id=${savedPropId}` : ''}`;
    if (wpReturnUrl) nextUrl += `&return_url=${encodeURIComponent(wpReturnUrl)}`;
    if (wpShopUrl) nextUrl += `&shop_url=${encodeURIComponent(wpShopUrl)}`;

    window.location.href = `${API}/auth/google?next=${encodeURIComponent(nextUrl)}&linkToken=${encodeURIComponent(token)}`;
  };

  const handleSelectAndSaveGA4 = useCallback(async (newPropId) => {
    setPropId(newPropId);
    setProcessing(true);
    setError('');
    try {
      if (!newPropId) {
        throw new Error('Please select a GA4 property.');
      }

      const res = await fetch(`${API}/integrations/property`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ propertyId: newPropId }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Failed to save GA4 property: ${txt}`);
      }
      setSavedPropId(newPropId);
      setMessage('Google Analytics 4 Property ID saved successfully!');
      setIsError(false);
      setToastActive(true);

    } catch (err) {
      console.error('[ConnectWooCommerce] Error saving GA4 property:', err);
      setError(err.message || 'Error saving GA4 property.');
      setMessage(`Error saving GA4 Property: ${err.message}`);
      setIsError(true);
      setToastActive(true);
    } finally {
      setProcessing(false);
    }
  }, [token]);

  const handleDisconnectGA4 = useCallback(async () => {
    if (!window.confirm("Are you sure you want to disconnect your Google Analytics account?")) {
      return;
    }

    setDisconnectingGa4(true);
    setError('');

    try {
      const res = await fetch(`${API}/integrations/disconnect-ga4-woocommerce`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Disconnect failed: ${txt}`);
      }

      resetGa4IntegrationStates();
      setMessage('Google Analytics account disconnected. Please re-connect.');
      setIsError(false);
      setToastActive(true);

      // Redirect to Google OAuth - preserve WordPress params
      let nextUrl = `/connect-woocommerce?siteId=${site.id}&siteToken=${encodeURIComponent(site.token)}${savedPropId ? `&ga4Id=${savedPropId}` : ''}`;
      if (wpReturnUrl) nextUrl += `&return_url=${encodeURIComponent(wpReturnUrl)}`;
      if (wpShopUrl) nextUrl += `&shop_url=${encodeURIComponent(wpShopUrl)}`;

      window.location.href = `${API}/auth/google?next=${encodeURIComponent(nextUrl)}&linkToken=${encodeURIComponent(token)}`;

    } catch (e) {
      console.error('[ConnectWooCommerce] Disconnect GA4 error', e);
      setError(`Failed to disconnect: ${e.message}`);
      setDisconnectingGa4(false);
      setMessage(`Failed to disconnect GA4: ${e.message}`);
      setIsError(true);
      setToastActive(true);
    }
  }, [token, site.id, site.token, savedPropId, wpReturnUrl, wpShopUrl, resetGa4IntegrationStates]);

  const handleRegisterAndDownload = useCallback(async () => {
    setError('');
    setRegistering(true);
    try {
      sessionStorage.setItem('new_site_added', 'true');
      window.location.href = pluginUrl;
    } catch (err) {
      console.error('Connect WooCommerce failed:', err);
      setError(`Could not initiate WooCommerce plugin download: ${err.message}`);
      sessionStorage.removeItem('new_site_added');
    } finally {
      setRegistering(false);
    }
  }, [pluginUrl]);

  // Navigation
  const goNext = () => setSlideIndex((prev) => Math.min(prev + 1, 2));
  const goBack = () => setSlideIndex((prev) => Math.max(prev - 1, 0));

  const toggleToastActive = useCallback(() => setToastActive((active) => !active), []);
  const toastMarkup = toastActive ? (
    <Toast content={message} error={isError} onDismiss={toggleToastActive} duration={5000} />
  ) : null;

  // ============================================
  //  RENDER
  // ============================================

  if (creatingSite || loading) {
    return (
      <Page title="Set Up WooCommerce">
        <div className={styles.page}>
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <p className={styles.statusText}>{creatingSite ? 'Setting up your site...' : 'Loading...'}</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  if (error && (!site.id || !site.token) && !isFromWordPress) {
    return (
      <Page title="Set Up WooCommerce" backAction={{ onAction: () => navigate('/dashboard') }}>
        <div className={styles.page}>
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <p className={styles.errorText}>{error}</p>
              <p className={styles.helpText}>Please ensure you started from the "Add Site" page.</p>
              <button className={styles.btnPrimary} onClick={() => navigate('/sites')}>
                Go to Add Site
              </button>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  // Step Tracker - based on actual progress
  const StepTracker = () => {
    const getStepStatus = (stepNum) => {
      if (stepNum === 1) return 'complete'; // Install always done
      if (stepNum === 2) return ga4Connected ? 'complete' : (slideIndex >= 1 ? 'current' : 'pending');
      if (stepNum === 3) return isComplete ? 'complete' : (slideIndex >= 2 ? 'current' : 'pending');
      return 'pending';
    };

    return (
      <div className={styles.stepTracker}>
        {/* Step 1: Install */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles.complete}`}>
            <CheckIcon />
          </div>
          <span className={`${styles.stepLabel} ${styles.complete}`}>Install</span>
        </div>

        <div className={styles.stepLine}>
          <div className={styles.stepLineProgress} style={{ width: ga4Connected ? '100%' : '0%' }} />
        </div>

        {/* Step 2: Connect */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles[getStepStatus(2)]}`}>
            {ga4Connected ? <CheckIcon /> : <AnalyticsIcon />}
          </div>
          <span className={`${styles.stepLabel} ${styles[getStepStatus(2)]}`}>Connect</span>
        </div>

        <div className={styles.stepLine}>
          <div className={styles.stepLineProgress} style={{ width: isComplete ? '100%' : '0%' }} />
        </div>

        {/* Step 3: Download */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles[getStepStatus(3)]}`}>
            {isComplete ? <CheckIcon /> : <DownloadIcon />}
          </div>
          <span className={`${styles.stepLabel} ${styles[getStepStatus(3)]}`}>Download</span>
        </div>
      </div>
    );
  };

  // Card 1: Welcome/Onboarding
  const renderWelcomeSlide = () => (
    <div className={styles.slide}>
      <OnboardingBanner
        currentStep={slideIndex + 1}
        onNextStep={goNext}
        onDismiss={() => navigate('/dashboard')}
      />
    </div>
  );

  // Card 2: Connect Google + Select GA4 Property
  const renderGA4Slide = () => (
    <div className={styles.slide}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>Add Google Analytics <span className={styles.optionalTag}>Optional</span></h2>
          <p>BaoBrain already tracks your store on its own. Linking GA4 layers in your historical Google data for deeper insights.</p>
        </div>

        <div className={styles.cardBody}>
          {!ga4Connected ? (
            <>
              <p className={styles.helpText}>
                If you already use Google Analytics, link it now so BaoBrain can pull your GA4 data in alongside its own tracking.
              </p>
              <button className={styles.btnPrimary} onClick={handleConnectGoogle}>
                <GoogleIcon /> Connect Google Account
              </button>
              <p className={styles.helpText}>
                Not using Google Analytics? Skip this step — BaoBrain's own tracking captures everything you need.
              </p>
            </>
          ) : propsLoading ? (
            <>
              <div className={styles.loadingSpinner} />
              <p className={styles.statusText}>Looking for your GA4 properties…</p>
              <p className={styles.helpText}>This usually takes a few seconds.</p>
            </>
          ) : (
            <>
              <div className={styles.connectedBadge}>
                <CheckIcon size={16} />
                <span>Google connected — GA4 data will layer in</span>
              </div>

              {propsList.length > 0 ? (
                <>
                  <div className={styles.selectGroup}>
                    <label htmlFor="ga4-property">Choose your Analytics property</label>
                    <select
                      id="ga4-property"
                      value={propId}
                      onChange={(e) => handleSelectAndSaveGA4(e.target.value)}
                      disabled={processing}
                    >
                      {propsList.map((p) => (
                        <option key={p.measurementId} value={p.measurementId}>
                          {p.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className={styles.helpText}>
                    Don't see the right one?{' '}
                    <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer">
                      Open Google Analytics
                    </a>
                  </p>
                </>
              ) : (
                <div className={styles.fallbackBox}>
                  <p className={styles.fallbackTitle}>No Analytics properties on this account</p>
                  <p className={styles.helpText}>
                    No problem — you can skip this step and still get full BaoBrain insights from our pixel.
                  </p>
                  <a
                    href="https://support.google.com/analytics/answer/9304153"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.fallbackLink}
                  >
                    Don't have Google Analytics? Set it up in 2 minutes →
                  </a>
                </div>
              )}

              <button className={styles.btnText} onClick={handleDisconnectGA4} disabled={disconnectingGa4}>
                {disconnectingGa4 ? 'Switching…' : 'Use a different Google account'}
              </button>
            </>
          )}

          {error && <p className={styles.errorText}>{error}</p>}
        </div>

        <div className={styles.cardFooter}>
          <button className={styles.btnSecondary} onClick={goBack}>
            <ArrowLeftIcon /> Back
          </button>
          {ga4Connected && !propsLoading && propsList.length === 0 ? (
            <button className={styles.btnPrimary} onClick={goNext}>
              Skip for now <ArrowRightIcon />
            </button>
          ) : (
            <button className={styles.btnPrimary} onClick={goNext} disabled={propsLoading}>
              {propId ? 'Next' : 'Skip for now'} <ArrowRightIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Card 3: Download Plugin
  const renderDownloadSlide = () => {
    // Resolve human-readable property name
    const selectedProperty = propsList.find(
      (p) =>
        p.measurementId === propId ||
        p.propertyId === propId ||
        String(p.propertyId) === String(propId) ||
        String(p.measurementId) === String(propId)
    );
    const propertyDisplayName = selectedProperty?.displayName || 'your Analytics property';

    return (
    <div className={styles.slide}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>Almost there — one quick install</h2>
          <p>Download the BaoBrain plugin, drop it into WordPress, and you'll unlock everything below</p>
        </div>

        <div className={styles.cardBody}>
          {isFromWordPress ? (
            <>
              <p className={styles.helpText}>
                Click below to complete setup and return to WordPress with your credentials.
              </p>
              <button
                className={styles.btnPrimary}
                onClick={() => {
                  if (redirectToWordPress()) return;
                  navigate('/dashboard');
                }}
                disabled={!site.id || !site.token}
              >
                Complete Setup in WordPress
              </button>
            </>
          ) : (
            <>
              {propId ? (
                <div className={styles.summaryBox}>
                  <p className={styles.summaryLabel}>Connected to</p>
                  <p className={styles.summaryValue}>{propertyDisplayName}</p>
                </div>
              ) : null}

              <ul className={styles.unlockList}>
                <li><CheckIcon size={16} /> Real-time visitor &amp; session tracking</li>
                <li><CheckIcon size={16} /> Conversion funnel + drop-off detection</li>
                {propId && <li><CheckIcon size={16} /> GA4 historical data layered in</li>}
                <li><CheckIcon size={16} /> Customer intelligence dashboards</li>
              </ul>

              <p className={styles.helpText}>
                Your plugin is pre-configured — just download and activate it on WordPress.
              </p>

              <button
                className={styles.btnPrimary}
                onClick={handleRegisterAndDownload}
                disabled={!site.id || !site.token || registering}
              >
                <DownloadIcon /> {registering ? 'Downloading…' : 'Download plugin (.zip)'}
              </button>

              {(site.id && site.token) && (
                <div className={styles.installSteps}>
                  <p className={styles.helpText}><strong>Quick install:</strong></p>
                  <p className={styles.helpText}>
                    1. In WordPress: Plugins → Add New → Upload Plugin<br/>
                    2. Select the .zip file and click Install Now<br/>
                    3. Activate the plugin and click Connect in WooCommerce → BaoBrain
                  </p>
                </div>
              )}
            </>
          )}

          {error && (
            <div className={styles.inlineAlertError}>
              <span className={styles.inlineAlertIcon}>!</span>
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className={styles.cardFooter}>
          <button className={styles.btnSecondary} onClick={goBack}>
            <ArrowLeftIcon /> Back
          </button>
          <button
            className={styles.btnPrimary}
            onClick={() => {
              if (propId || savedPropId) {
                setShowSuccessModal(true);
              } else {
                navigate('/dashboard');
              }
            }}
          >
            {isComplete ? 'Go to Dashboard' : 'Finish'}
          </button>
        </div>
      </div>
    </div>
    );
  };

  return (
    <Frame>
      <Page
        title="Set up your store"
        backAction={{ content: 'Back to Dashboard', onAction: () => navigate('/dashboard') }}
      >
        <div className={styles.page}>
          <StepTracker />

          {toast && (
            <div className={styles.toast} key={toast.key} role="status" aria-live="polite">
              <span className={styles.toastIcon}>
                <CheckIcon size={14} />
              </span>
              <span>{toast.msg}</span>
            </div>
          )}

          <div className={styles.carousel}>
            <div className={styles.track} style={{ transform: `translateX(-${slideIndex * 100}%)` }}>
              {renderWelcomeSlide()}
              {renderGA4Slide()}
              {renderDownloadSlide()}
            </div>
          </div>

          <div className={styles.support}>
            Need help?{' '}
            <a href="https://app.baobrain.com/installation" target="_blank" rel="noopener noreferrer">Setup Guide</a>
            {' · '}
            <a href="/help" target="_blank" rel="noopener noreferrer">Contact Support</a>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className={styles.modalOverlay} onClick={() => setShowSuccessModal(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <FlashCardCongrats
                title="Property saved — let's finish up"
                shopDomain={site.url || null}
                message={
                  <>
                    <p style={{ marginBottom: 12 }}>
                      Your Google Analytics property is connected. One last step: download the BaoBrain plugin and install it on WordPress to flip everything on.
                    </p>
                    <p style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
                      💡 Once the plugin is active, visit your own site in a new tab — you'll appear in your dashboard within seconds.
                    </p>
                  </>
                }
                ctaLabel="Continue to download"
                onCta={() => setShowSuccessModal(false)}
                onClose={() => setShowSuccessModal(false)}
                showBackfill={true}
                onBackfill={async () => {
                  try {
                    const siteIdToUse = site.id || activeSite?.id || Number(sessionStorage.getItem('woocommerce_site_id'));

                    if (!siteIdToUse) {
                      alert('Could not determine site ID. Please refresh the page and try again.');
                      return;
                    }

                    const res = await fetch(
                      `${API}/api/customer-intelligence/backfill-orders?site_id=${siteIdToUse}&days=365`,
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                      }
                    );

                    if (!res.ok) {
                      const errorData = await res.json().catch(() => ({}));
                      throw new Error(errorData.detail || `Failed to start backfill (${res.status})`);
                    }

                    alert('Historical order import started! This may take a few minutes.');
                    setShowSuccessModal(false);

                    if (isFromWordPress && redirectToWordPress()) {
                      return;
                    }

                    navigate('/dashboard');

                  } catch (error) {
                    alert('Failed to start backfill: ' + error.message);
                  }
                }}
              />
            </div>
          </div>
        )}
      </Page>
      {toastMarkup}
    </Frame>
  );
}